<template>
  <b-container fluid class="bg-white shadow px-3 pb-5 px-6-lg pb-2-lg w-lg-70">
    <h2 class="py-3">Contacts</h2>
    <template v-if="!loading">
      <b-card>
        <b-form-row>
          <h5 class="col-12 text-primary font-weight-bold pb-2">
            Landowner Information (Required)
          </h5>
        </b-form-row>
        <template v-if="!notification.landowner && !notification.landownerUser">
          <FindContactForm
            :notification="notification"
            title="Landowner"
            type="landowner"
            :findActionName="'Notification/updateLandowner'"
            :contactError="loError"
          />
        </template>
        <template v-if="!notification.landowner
          && !notification.landownerUser
          && (notification.certificationMethodId === 2
          || notification.certificationMethodId === 3)">
          <b-col cols="12" :class="isExtraSmall ? 'text-center' : ''">
            <h5 class="text-primary font-weight-bold pb-2">-- or --</h5>
          </b-col>
          <b-button
            pill
            class="mb-3 w-100 w-sm-auto"
            @click="openCreateNewContact" aria-label="Enter Landowner Info">
            Enter Landowner Info
          </b-button>
          <b-modal
            ref="newManagedLandownerModal"
            id="newManagedLandownerModal"
            centered
            size="xl"
            title="Enter Landowner Info"
            no-close-on-backdrop
            hide-header-close
            no-stacking>
            <NewManagedLandownerForm
              ref="submitNewManagedLandownerForm"
              :edit="false"
              :withinModal="true" />
            <div slot="modal-footer" class="d-flex">
              <b-button
                pill
                variant="danger"
                class="mr-3"
                :disabled="submitting"
                @click="cancelNewManagedLandowner" aria-label="Cancel entering Landowner Info">
                Cancel
              </b-button>
              <b-button
                pill
                variant="primary"
                :disabled="mfsAdmin || submitting"
                @click="handleSubmitNewManagedLandowner" aria-label="Create Managed Landowner">
                Create Managed Landowner
              </b-button>
            </div>
          </b-modal>
        </template>
        <span class="font-sm" v-if="notification.landowner">
          {{userRoleMessage("Landowner")}}
          <hr>
        </span>
        <template v-if="notification.landownerUser">
          <ContactDetails
            :contact="notification.landownerUser"
            :removeActionName="'Notification/removeLandowner'"
            :disableRemove="notification.landowner"
          />
        </template>
        <hr>
        <OwnershipForm :notification="notification" ref="ownershipForm" />
      </b-card>
      <b-card>
        <b-form-row>
          <h5 class="col-12 text-primary font-weight-bold pb-2">
            Designated Agent Information{{!notification.designatedAgent ? ' (Optional)' : ''}}
          </h5>
        </b-form-row>
        <template v-if="!notification.designatedAgent && !notification.designatedAgentUser">
          <FindContactForm
            :notification="notification"
            title="Designated Agent"
            type="designatedAgent"
            :findActionName="'Notification/updateDesignatedAgent'"
            :contactError="daError"
            :optional="!notification.designatedAgent"
          />
        </template>
        <span v-if="notification.designatedAgent" class="font-sm">
          {{userRoleMessage("Designated Agent")}}
          <hr>
        </span>
        <template v-if="notification.designatedAgentUser">
          <ContactDetails
            :removeActionName="'Notification/removeDesignatedAgent'"
            :contact="notification.designatedAgentUser"
            :disableRemove="notification.designatedAgent"
          />
        </template>
      </b-card>
      <b-card>
        <b-form-row>
          <h5 class="col-12 text-primary font-weight-bold pb-2">
            Harvester/Operator Information (Required)
          </h5>
        </b-form-row>
        <template v-if="!notification.operator && !notification.operatorUser">
          <FindContactForm
            :notification="notification"
            title="Harvester/Operator"
            type="operator"
            :findActionName="'Notification/updateOperator'"
            :contactError="operatorError"
          />
        </template>
        <span v-if="notification.operator" class="font-sm">
          {{userRoleMessage("Harvester/Operator")}}
          <hr>
        </span>
        <template v-if="notification.operatorUser">
          <ContactDetails
            :removeActionName="'Notification/removeOperator'"
            :contact="notification.operatorUser"
            :disableRemove="notification.operator"
          />
        </template>
      </b-card>
      <b-card>
        <b-form-row>
          <h5 class="col-12 text-primary font-weight-bold pb-2">
            Licensed Forester Information{{!notification.licensedForester ? ' (Optional)' : ''}}
          </h5>
        </b-form-row>
        <template v-if="!notification.licensedForester && !notification.licensedForesterUser">
          <FindContactForm
            :notification="notification"
            title="Licensed Forester"
            type="licensedForester"
            :findActionName="'Notification/updateLicensedForester'"
            :contactError="lfError"
            :optional="!notification.licensedForester"
          />
        </template>
        <span v-if="notification.licensedForester" class="font-sm">
          {{userRoleMessage("Licensed Forester")}}
          <hr>
        </span>
        <template v-if="notification.licensedForesterUser">
          <ContactDetails
            :removeActionName="'Notification/removeLicensedForester'"
            :contact="notification.licensedForesterUser"
            :disableRemove="notification.licensedForester"
          />
        </template>
      </b-card>
    </template>
  </b-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import NewManagedLandownerForm from '@/components/account/NewManagedLandownerForm.vue';
import ContactDetails from '@/components/account/ContactDetails.vue';
import FindContactForm from '@/components/notification/FindContactForm.vue';
import OwnershipForm from '@/components/notification/OwnershipForm.vue';

export default {
  name: 'ContactsForm',
  components: {
    NewManagedLandownerForm,
    ContactDetails,
    FindContactForm,
    OwnershipForm,
  },
  data() {
    return {
      submitting: false,
    };
  },
  computed: {
    ...mapState('Notification', {
      loading: state => state.loading,
      notification: state => state.notification,
      daError: state => state.daError,
      loError: state => state.loError,
      operatorError: state => state.operatorError,
      lfError: state => state.lfError,
    }),
    ...mapState(['isExtraSmall']),
    ...mapGetters('User', ['mfsAdmin']),
    contactsRequired() {
      return !this.notification.landownerUser || !this.notification.operatorUser;
    },
  },
  methods: {
    userRoleMessage(roleType) {
      return `You indicated that you are the ${roleType} for this notification on the Overview page.
        If this is an error, please return to the Overview page and make the correction. If your
        contact details displayed here are incorrect, please update them in your account profile.`;
    },
    openCreateNewContact() {
      this.$refs.newManagedLandownerModal.show();
    },
    async handleSubmitNewManagedLandowner(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.submitting = true;
      await this.$refs.submitNewManagedLandownerForm.submitWrapper();
      this.submitting = false;
    },
    cancelNewManagedLandowner() {
      this.submitting = false;
      this.$refs.newManagedLandownerModal.hide();
    },
    async submitWrapper() {
      if (this.mfsAdmin) {
        return true;
      }
      const ownershipSubmitted = await this.$refs.ownershipForm.submitWrapper();
      return ownershipSubmitted && !this.contactsRequired;
    },
  },
};
</script>
